import type { Proposta, PropostaItem } from '@/types'
import { normalizePropostaForExport, resolvePropostaItems } from '@/utils/normalizeProposta'
import type { LayoutConfig, PlanData, PlanServiceItem } from '@/utils/dealCalculations'
import {
  defaultLayoutConfig,
  extractServices,
} from '@/utils/dealCalculations'
import { normalizeInitialLayout } from '@/components/Deals/ProposalStudio/normalizeInitialLayout'
import { formatCurrency } from '@/utils/format-currency'
import { recurringSetupEntry } from '@/utils/recurringSetupEntry'
import { formatCategoryDisplayTitle, normalizeTextEncoding } from '@/utils/text-normalization'
import { formatSlideDescriptionBody } from '@/components/Deals/ProposalStudio/slidePreviewTheme'
import {
  buildSlideLayoutForProposalItem,
  enrichExportLayoutConfig,
  findCatalogPlanForItem,
  resolveProposalItemScope,
  type CatalogPlan,
} from '@/utils/planCatalog'
import {
  resolveSupportHoursForSlide,
  resolveSupportTiersFromPlan,
} from '@/utils/planSuportePricing'

export function resolveProposalLayout(proposal: Proposta): LayoutConfig | undefined {
  const raw = proposal as Proposta & { layout?: LayoutConfig }
  if (raw.layout && typeof raw.layout === 'object') {
    return raw.layout
  }
  try {
    if (raw.dados) {
      const parsed = JSON.parse(raw.dados)
      if (parsed.layout && typeof parsed.layout === 'object') {
        return parsed.layout as LayoutConfig
      }
    }
  } catch {
    /* ignore */
  }
  return undefined
}

export function buildPaymentDetailsFromItem(item: PropostaItem): string {
  const pricing = item.pricing
  if (!pricing) {
    return `${formatCurrency(item.valorUnitario)}`
  }
  if (pricing.recorrente) {
    const monthly = formatCurrency(pricing.installmentValue || item.valorUnitario)
    const setup = recurringSetupEntry(pricing)
    if (setup > 0) {
      return `Entrada: ${formatCurrency(setup)} + ${pricing.installments}x de ${monthly}`
    }
    return `${monthly} / mês`
  }
  return `${pricing.installments || 1}x de ${formatCurrency(pricing.installmentValue || item.valorUnitario)}`
}

/** Plano mensal já mostra o valor em “Investimento mensal”. A linha de entrada só repete a mensalidade. */
export function resolveSlidePaymentDetails(
  saved: string | undefined,
  item: PropostaItem | null | undefined,
): string {
  const trimmed = saved?.trim() ?? ''
  if (trimmed && !trimmed.startsWith('Entrada:')) return trimmed
  if (item?.pricing?.recorrente) return ''
  return item ? buildPaymentDetailsFromItem(item) : ''
}

export function buildLoyaltyLabelFromItem(item: PropostaItem): string {
  const duration = item.duration || item.pricing?.duracao || 12
  if (item.pricing?.recorrente || item.tipo === 'plano' || item.tipo === 'servico') {
    return `Fidelidade: ${duration} meses`
  }
  return `Duração: ${duration} meses`
}

export function buildInvestmentLabelFromItem(item: PropostaItem): string {
  const pricing = item.pricing
  const isRecorrente =
    pricing?.recorrente ?? (item.tipo === 'plano' || item.tipo === 'servico')

  if (isRecorrente) {
    const monthly = Number(pricing?.installmentValue ?? item.valorUnitario ?? 0)
    const total = Number(pricing?.total ?? item.total ?? 0)
    const installments = pricing?.installments || item.duration || 12
    if (total > monthly * 1.05 && installments > 1) {
      return formatCurrency(total)
    }
    return `${formatCurrency(monthly)}/mês`
  }

  const total = Number(pricing?.total ?? item.total ?? 0)
  const installments = pricing?.installments || 1
  const installmentValue = Number(pricing?.installmentValue ?? item.valorUnitario ?? 0)
  if (pricing?.entry && pricing.entry > 0) {
    return `${formatCurrency(total)} (entrada ${formatCurrency(pricing.entry)} + ${installments}x de ${formatCurrency(installmentValue)})`
  }
  if (installments > 1 && installmentValue > 0) {
    return `${formatCurrency(total)} (${installments}x de ${formatCurrency(installmentValue)})`
  }
  return formatCurrency(total)
}

export function proposalItemToServicos(item: PropostaItem): PlanServiceItem[] {
  return extractServices(item).flatMap((group) =>
    (group.services || [])
      .filter((svc: { isIncluded?: boolean; name?: string; nome?: string; quantity?: string | number; quantidade?: string | number; isPartial?: boolean }) => svc.isIncluded !== false)
      .map((svc: { isIncluded?: boolean; name?: string; nome?: string; quantity?: string | number; quantidade?: string | number; isPartial?: boolean }) => ({
        id: `${item.id}-${svc.name || svc.nome}`,
        nome: normalizeTextEncoding(svc.name || svc.nome || ''),
        valor: 0,
        categoria: formatCategoryDisplayTitle(group.category || group.categoria || 'Outros'),
        recorrente: item.tipo === 'servico' || item.tipo === 'plano',
        isPartial: svc.isPartial,
        quantity: svc.quantity || svc.quantidade,
      })),
  )
}

export interface BuildExportPlanDataOptions {
  dealServico?: string
  catalogPlans?: CatalogPlan[]
}

/**
 * Monta PlanData para PDF/PPTX alinhado ao Proposal Studio (escopo do catálogo + layout salvo).
 */
export function buildExportPlanData(
  proposal: Proposta,
  options: BuildExportPlanDataOptions = {},
): PlanData {
  const normalized = normalizePropostaForExport(proposal)
  const { dealServico, catalogPlans = [] } = options
  const proposalItems = resolvePropostaItems(normalized)
  const primaryItemRaw = proposalItems[0]
  const scope = primaryItemRaw
    ? resolveProposalItemScope(primaryItemRaw, catalogPlans)
    : []
  const primaryItem = primaryItemRaw ? { ...primaryItemRaw, services: scope } : undefined

  const servicos = primaryItem ? proposalItemToServicos(primaryItem) : []
  const savedLayout = resolveProposalLayout(proposal)
  const layoutMeta = savedLayout as LayoutConfig & {
    paymentDetails?: string
    loyaltyDuration?: string
    description?: string
    bonusText?: string
  }

  const master = primaryItemRaw ? findCatalogPlanForItem(primaryItemRaw, catalogPlans) : undefined
  const planLabel =
    primaryItemRaw?.descricao ||
    primaryItemRaw?.nome ||
    (primaryItemRaw as PropostaItem & { name?: string })?.name ||
    master?.nome ||
    dealServico ||
    'Plano'
  const layoutConfig = primaryItemRaw
    ? enrichExportLayoutConfig(
        buildSlideLayoutForProposalItem(primaryItemRaw, catalogPlans, savedLayout),
        planLabel,
      )
    : enrichExportLayoutConfig(normalizeInitialLayout(savedLayout ?? defaultLayoutConfig), planLabel)

  const pricing = primaryItem?.pricing
  const totalFinal = Number(normalized.totalFinal ?? normalized.valor ?? 0)
  const duracao = Number(
    primaryItem?.duration ?? pricing?.duracao ?? pricing?.installments ?? 12,
  )
  let installmentValue = Number(
    pricing?.installmentValue ?? primaryItem?.valorUnitario ?? 0,
  )
  const tipoRaw = String(primaryItem?.tipo ?? '').toLowerCase()
  const isRecorrente =
    pricing?.recorrente ??
    (tipoRaw === 'plano' || tipoRaw === 'servico' || tipoRaw === 'recorrente')

  if (installmentValue <= 0 && totalFinal > 0 && isRecorrente && duracao > 0) {
    installmentValue = totalFinal / duracao
  }
  if (installmentValue <= 0 && totalFinal > 0) {
    installmentValue = totalFinal
  }

  const planName =
    primaryItem?.descricao ||
    primaryItem?.nome ||
    (primaryItem as PropostaItem & { name?: string })?.name ||
    layoutMeta?.planName ||
    dealServico ||
    'Plano'
  const planDescription =
    primaryItem?.description ||
    layoutMeta?.description ||
    ''
  const itemTipo = primaryItem?.tipo
  const projectType =
    primaryItem?.projectType ||
    (master?.config?.projectType as string) ||
    ''

  const bonusRaw = layoutMeta?.bonusText ?? primaryItem?.bonus ?? ''
  const showBonus = primaryItem?.showBonus === true && !!bonusRaw
  const supportHours = primaryItem
    ? resolveSupportHoursForSlide(primaryItem, catalogPlans)
    : 0
  const supportTiersRaw = primaryItem ? resolveSupportTiersFromPlan(primaryItem) : []
  const supportTiersForExport =
    supportTiersRaw.length >= 2
      ? supportTiersRaw.map((t) => ({
          supportHours: t.supportHours,
          monthlyValue: t.monthlyValue,
        }))
      : undefined

  return {
    id: proposal.id,
    nome: normalizeTextEncoding(planName),
    descricao: formatSlideDescriptionBody(normalizeTextEncoding(planDescription)),
    itemTipo,
    projectType: normalizeTextEncoding(projectType),
    tipo: normalizeTextEncoding(projectType),
    servicos,
    pricing: {
      valor: totalFinal || installmentValue,
      valorMensal: installmentValue,
      valorSetup: proposalItems
        .filter((i) => i.tipo === 'avulso')
        .reduce((acc, i) => acc + i.total, 0),
      desconto: normalized.desconto,
      total: totalFinal,
      recorrente: isRecorrente,
      duracao,
    },
    validade: normalized.validade,
    observacoes: normalized.observacoes,
    showBonus,
    bonusText: normalizeTextEncoding(bonusRaw),
    paymentDetails: resolveSlidePaymentDetails(layoutMeta?.paymentDetails, primaryItem),
    loyaltyDuration:
      layoutMeta?.loyaltyDuration ||
      (primaryItem ? buildLoyaltyLabelFromItem(primaryItem) : undefined),
    negotiationValidUntil: primaryItem?.negotiationValidUntil || undefined,
    supportHours: supportHours > 0 ? supportHours : undefined,
    supportTiers: supportTiersForExport,
    layoutConfig,
  }
}

export function buildExportPlanDataForItem(
  proposal: Proposta,
  item: PropostaItem,
  options: BuildExportPlanDataOptions = {},
): PlanData {
  const normalized = normalizePropostaForExport(proposal)
  const mini: Proposta = {
    ...normalized,
    itens: [item],
    items: [item],
    totalFinal: item.total,
    valor: item.total,
  }
  return buildExportPlanData(mini, options)
}
